import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Button from "@mui/material/Button";
import Header from "components/Header";
import { toast } from "react-toastify";
import { Box, Dialog, DialogTitle, DialogActions, DialogContent, TextField, Grid, FormControlLabel, Switch, Tooltip, Chip } from "@mui/material";
import { getAllEmployees, deleteProperty, getAllOwners, deleteEmployee, terminateEmployeeApi } from "state/apis";
import BlockIcon from "@mui/icons-material/Block";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import Add from "./Add";
import TitlebarImageList from "./Document";
import { InputBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GetAppIcon from "@mui/icons-material/GetApp";
import { GiWallet } from "react-icons/gi";
import { useDispatch } from "react-redux";
import { getDefaultProperty } from "state";
import moment from "moment";

const Employee = () => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [totalData, setTotalData] = React.useState([]);
    const [openAddForm, setOpenAddForm] = React.useState(false);
    const [openDocList, setOpenDocList] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [deleteItem, setDeleteItem] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(50);
    const [searchQuery, setSearchQuery] = React.useState("");
    const inputRef = React.useRef(null);
    const dispatch = useDispatch();
    const [role, setRole] = React.useState({});
    const [showInactive, setShowInactive] = React.useState(false);
    const [terminateOpen, setTerminateOpen] = React.useState(false);
    const [terminateData, setTerminateData] = React.useState({
        leaveSalary: "",
        endOfService: "",
        terminationDate: "",
        terminationRemarks: "",
    });
    const [terminateEmployee, setTerminateEmployee] = React.useState(null);

    React.useEffect(() => {
        fetchData();
        fetchRole();
    }, [page, rowsPerPage, searchQuery, showInactive]);

    const fetchRole = async () => {
        const res = await getDefaultProperty(dispatch);
        if (res?.data?.role && res?.data?.role?.permission) {
            setRole(JSON.parse(res?.data?.role?.permission));
        }
    };

    const fetchData = async () => {
        try {
            // setIsLoading(true);
            const res = await getAllEmployees(rowsPerPage, page * rowsPerPage, searchQuery, showInactive);
            setData(res?.data);
            setTotalData(res?.totalCount);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to fetch data. Please try again.");
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleAddProperty = () => {
        setSelectedRow(null);
        setOpenAddForm(true);
    };

    const handleCloseAddForm = () => {
        setOpenAddForm(false);
    };

    const handleSubmitAdd = async () => {
        setOpenAddForm(false);
        setIsLoading(true);
        fetchData();
    };

    const handleEditClick = (row) => {
        setSelectedRow(row);
        setOpenAddForm(true);
    };

    const handleDocClick = (row) => {
        setSelectedRow(row);
        setOpenDocList(true);
    };
    const handleCloseDocList = () => {
        setOpenDocList(false);
    };

    const handleDeleteClick = (row) => {
        setDeleteItem(row?.id);
        setDeleteConfirmationOpen(true);
    };

    const handleDeleteConfirmation = async () => {
        try {
            await deleteEmployee(deleteItem);
            toast.success("Employee deleted successfully.");
            fetchData();
            setDeleteConfirmationOpen(false);
        } catch (error) {
            console.error("Error deleting employee:", error);
            toast.error("Failed to delete employee. Please try again.");
        }
    };

    const handleTerminateClick = (row) => {
        setTerminateEmployee(row);
        setTerminateData({
            leaveSalary: "",
            endOfService: "",
            terminationDate: "",
            terminationRemarks: "",
        });
        setTerminateOpen(true);
    };

    const handleTerminateSubmit = async () => {
        try {
            await terminateEmployeeApi(terminateEmployee.id, {
                isActive: false,
                ...terminateData,
            });
            toast.success("Employee terminated successfully.");
            setTerminateOpen(false);
            fetchData();
        } catch (error) {
            console.error("Error terminating employee:", error);
            toast.error("Failed to terminate employee.");
        }
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleSearch = () => {
        fetchData();
    };

    const focusInput = () => {
        inputRef.current.focus();
    };

    if (isLoading) {
        return (
            <Box sx={{ p: "24px 28px" }}>
                <Header title="Employees" />
                <Box sx={{ mt: 4, textAlign: "center", color: "#5a6a85" }}>Loading...</Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: "24px 28px" }}>
            <Header title="Employees" subtitle="List of Employee" />
            <Paper sx={{ width: "100%", overflow: "hidden", mt: 2.5, borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <Box display="flex" justifyContent="flex-end" p={2}>
                    <Button
                        href={process.env.REACT_APP_BASE_URL + "employees/download-csv"}
                        target="_blank"
                        variant="contained"
                        color="primary"
                        sx={{ marginRight: "auto" }}
                        type="button"
                    >
                        CSV <GetAppIcon></GetAppIcon>
                    </Button>
                    <InputBase
                        placeholder="Search..."
                        sx={{
                            backgroundColor: "#eef1f6",
                            borderRadius: "8px",
                            px: 1.5,
                            py: 0.5,
                            fontSize: "0.8125rem",
                            width: 200,
                        }}
                        inputRef={inputRef}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={focusInput}
                    />
                    <IconButton onClick={handleSearch}>
                        <SearchIcon />
                    </IconButton>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showInactive}
                                onChange={(e) => {
                                    setShowInactive(e.target.checked);
                                    setPage(0);
                                }}
                                color="primary"
                                size="small"
                            />
                        }
                        label={<span style={{ fontSize: "0.8rem" }}>Show Inactive</span>}
                        sx={{ mr: 1 }}
                    />
                    {role?.employee?.add && (
                        <Button variant="contained" color="primary" onClick={handleAddProperty}>
                            Add Employee
                        </Button>
                    )}
                </Box>
                <TableContainer sx={{ maxHeight: 900 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>SL No</TableCell>
                                <TableCell>Emp Id</TableCell>
                                <TableCell>Employee Name</TableCell>
                                <TableCell>Joining Date</TableCell>
                                <TableCell>Designation</TableCell>
                                <TableCell>Salary</TableCell>
                                <TableCell>Gratuity Rec Up To</TableCell>
                                <TableCell>Passport Number</TableCell>
                                <TableCell>Passport Exp Date</TableCell>
                                <TableCell>Emirate Id</TableCell>
                                <TableCell>Emirates Id Exp Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Documents</TableCell>
                                {(role?.employee?.delete || role?.employee?.update) && (
                                    <TableCell>Actions</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.map((row, index) => (
                                <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{row?.empId}</TableCell>
                                    <TableCell>{row?.employeeName}</TableCell>
                                    <TableCell>
                                        {moment(row?.joiningDate).format("DD-MM-YYYY")}
                                    </TableCell>
                                    <TableCell>{row?.designation}</TableCell>
                                    <TableCell>{row?.salary}</TableCell>
                                    <TableCell>{row?.gratuityRecUpTo}</TableCell>
                                    <TableCell>{row?.passportNumber}</TableCell>
                                    <TableCell>
                                        {moment(row?.passportExpDate).format("DD-MM-YYYY")}
                                    </TableCell>
                                    <TableCell>{row?.emiratesId}</TableCell>
                                    <TableCell>
                                        {moment(row?.emiratesIdExpDate).format("DD-MM-YYYY")}
                                    </TableCell>
                                    <TableCell>
                                        {row?.isActive ? (
                                            <Chip label="Active" color="success" size="small" />
                                        ) : (
                                            <Tooltip title={`Terminated: ${row?.terminationDate ? moment(row.terminationDate).format("DD-MM-YYYY") : "-"}`} arrow>
                                                <Chip label="Inactive" color="error" size="small" />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleDocClick(row)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                    </TableCell>

                                    {(role?.employee?.delete || role?.employee?.update) && (
                                        <TableCell>
                                            {role?.employee?.update && (
                                                <IconButton onClick={() => handleEditClick(row)}>
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                            {role?.employee?.update && row?.isActive && (
                                                <Tooltip title="Terminate Employee" arrow>
                                                    <IconButton
                                                        color="warning"
                                                        onClick={() => handleTerminateClick(row)}
                                                    >
                                                        <BlockIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {role?.employee?.delete && row?.isActive && (
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDeleteClick(row)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalData}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
                <Add
                    open={openAddForm}
                    onClose={handleCloseAddForm}
                    onSubmit={handleSubmitAdd}
                    editData={selectedRow}
                    editMode={!!selectedRow}
                    isInactive={!!selectedRow && !selectedRow?.isActive}
                />
                <TitlebarImageList
                    open={openDocList}
                    onClose={handleCloseDocList}
                    editData={selectedRow}
                    editMode={!!selectedRow}
                />

                <Dialog open={terminateOpen} onClose={() => setTerminateOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle fontWeight="bold">
                        Terminate Employee - {terminateEmployee?.employeeName}
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Date of Termination"
                                        value={terminateData.terminationDate ? dayjs(terminateData.terminationDate) : null}
                                        onChange={(date) =>
                                            setTerminateData((prev) => ({
                                                ...prev,
                                                terminationDate: date ? date.format("YYYY-MM-DD") : "",
                                            }))
                                        }
                                        format="DD/MM/YYYY"
                                        sx={{ width: "100%", "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "primary.main" } }, "& .MuiInputLabel-root": { "&.Mui-focused": { color: "primary.main" } } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    color="primary"
                                    fullWidth
                                    label="Leave Salary"
                                    type="number"
                                    value={terminateData.leaveSalary}
                                    onChange={(e) =>
                                        setTerminateData((prev) => ({ ...prev, leaveSalary: e.target.value }))
                                    }
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    color="primary"
                                    fullWidth
                                    label="End of Service"
                                    type="number"
                                    value={terminateData.endOfService}
                                    onChange={(e) =>
                                        setTerminateData((prev) => ({ ...prev, endOfService: e.target.value }))
                                    }
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    color="primary"
                                    fullWidth
                                    label="Remarks"
                                    value={terminateData.terminationRemarks}
                                    onChange={(e) =>
                                        setTerminateData((prev) => ({ ...prev, terminationRemarks: e.target.value }))
                                    }
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setTerminateOpen(false)} sx={{ color: "#5a6a85" }}>
                            Cancel
                        </Button>
                        <Button onClick={handleTerminateSubmit} color="error" variant="contained">
                            Terminate
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={deleteConfirmationOpen}
                    onClose={() => setDeleteConfirmationOpen(false)}
                >
                    <DialogTitle fontWeight={"bold"}>Are you sure you want to delete?</DialogTitle>
                    <DialogActions>
                        <Button
                            onClick={() => setDeleteConfirmationOpen(false)}
                            sx={{ color: "#5a6a85" }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirmation} color="error" autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    );
};

export default Employee;
